import { initReactI18next } from "react-i18next";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";

import { withTimeout } from "@lib/withTimeout";

// Only the default namespace is loaded at init. `techniques.json` (623 KB
// raw, ~180 KB over the wire) holds the technique-page, series and profile
// copy that member pages read through `fallbackNS`; nothing on /checkout,
// /login, /gifts or /redeem references a key from it (their `label.*` and
// `footer.*` keys live in translations.json). It used to sit in `ns` for
// every non-checkout route, and because main.tsx mounts React only once
// i18next reports `initialized`, which waits for every namespace in `ns`,
// that download ran serially before the mount on every one of those pages.
// Member routes now start it from main.tsx right after init, in parallel
// with the auth check and the page data, and RequireTechniquesNamespace in
// routes.tsx holds the member tree until it has landed (OMG-1836).
i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: [
      "en",
      "de",
      "fr",
      "es",
      "es_419",
      "it",
      "nl",
      "pt",
      "ru",
      "ja",
      "ko",
      "zh_CN",
      "zh_HK",
    ],
    fallbackLng: "en",
    debug: false,
    ns: ["translations"],
    defaultNS: "translations",
    // Member-area components all call `useTranslation()` with no namespace
    // and reach technique-page copy, S2/S3 names, etc. through this
    // fallback. A fallback namespace is never fetched on demand by a lookup
    // (and react-i18next only re-renders on `languageChanged`, not when a
    // namespace lands later), which is why loadTechniquesNamespace below is
    // explicit and the member route tree waits on it.
    fallbackNS: "techniques",
    backend: {
      loadPath: `${import.meta.env.VITE_TRANSLATION_URL}/{{lng}}/{{ns}}.json`,
      requestOptions: {
        cache: "no-store",
      },
    },
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;

export const TECHNIQUES_NAMESPACE = "techniques";

// A stalled CDN must degrade to raw key names, as a failed init already does,
// never to a loader that stays up. Same cap as main.tsx's I18N_MOUNT_TIMEOUT_MS
// so the worst case for a member page stays where it was: the gate mounts
// after the auth check and gives up 3s later.
export const TECHNIQUES_LOAD_TIMEOUT_MS = 3_000;

const whenInitialized = (): Promise<void> =>
  i18n.isInitialized
    ? Promise.resolve()
    : new Promise((resolve) => {
        i18n.on("initialized", () => resolve());
      });

/**
 * Load `techniques.json` for the current language. Waits for init first:
 * language detection runs on a deferred tick inside init, and loadNamespaces
 * before that would resolve against no language. Deduped by i18next, so
 * calling it from main.tsx (early start) and from the route gate (wait) is
 * one request. Always resolves; a failure or timeout leaves lookups falling
 * through to raw key names, the same state a failed init produces.
 */
export const loadTechniquesNamespace = (): Promise<void> =>
  withTimeout(
    whenInitialized().then(() => i18n.loadNamespaces(TECHNIQUES_NAMESPACE)),
    TECHNIQUES_LOAD_TIMEOUT_MS,
    "techniques namespace",
  ).then(
    () => undefined,
    () => undefined,
  );

export const hasTechniquesNamespace = (): boolean =>
  i18n.isInitialized && i18n.hasLoadedNamespace(TECHNIQUES_NAMESPACE);
